import { Injectable, inject } from '@angular/core';
import { Web3Service } from './web3.service';
import { readContract, writeContract, waitForTransactionReceipt, multicall } from '@wagmi/core';
import { STAKING_ABI, TOKEN_ABI, CONTRACT_ADDRESSES } from '../constants/contracts';
import { parseUnits, formatUnits } from 'viem';

export interface StakeInfo {
  index: number;
  amount: string;
  pendingRewards: string;
  isMatured: boolean;
  remainingSeconds: number;
  formattedCountdown: string;
  progressPercent: number;
  penaltyPercent: number;
  penaltyAmount: string;
  claimableAmount: string;
  netWithdrawAmount: string;
}

export interface PoolUserData {
  totalUserStaked: string;
  totalUserRewards: string;
  userPoolExpiry: number;
  userStakeCount: number;
  userStakes: StakeInfo[];
}

@Injectable({
  providedIn: 'root'
})
export class StakingService {
  private web3Service = inject(Web3Service);

  /**
   * Universal Read Method for Staking Contract
   */
  public async getUserPoolAggregates(userAddress: `0x${string}`, poolId: bigint): Promise<PoolUserData> {
    try {
      const countObj = await this.readStakingContract('getUserStakesCount', [userAddress, poolId]);
      const count = Number(countObj || 0);
      
      if (count === 0) {
        return {
          totalUserStaked: '0',
          totalUserRewards: '0',
          userPoolExpiry: 0,
          userStakeCount: 0,
          userStakes: []
        };
      }

      const stakeIndices = Array.from({ length: count }, (_, i) => BigInt(i));
      
      // Batch fetch basic stake info using Multicall
      const stakeInfos = await this.multicallStaking(
        stakeIndices.map(idx => ({ functionName: 'getUserStake', args: [userAddress, poolId, idx] }))
      );

      const activeStakeIndices: bigint[] = [];
      stakeInfos.forEach((res, i) => {
        const info = res.result;
        const amount = info?.amount ?? info?.[0] ?? 0n;
        if (amount > 0n) activeStakeIndices.push(BigInt(i));
      });

      if (activeStakeIndices.length === 0) {
        return {
          totalUserStaked: '0',
          totalUserRewards: '0',
          userPoolExpiry: 0,
          userStakeCount: 0,
          userStakes: []
        };
      }

      // Batch fetch details for active stakes ONLY in ONE multicall
      const detailCalls: any[] = [];
      activeStakeIndices.forEach(idx => {
        detailCalls.push({ functionName: 'pendingRewards', args: [userAddress, poolId, idx] });
        detailCalls.push({ functionName: 'getMaturityStatus', args: [userAddress, poolId, idx] });
        detailCalls.push({ functionName: 'getPenaltyInfo', args: [userAddress, poolId, idx] });
      });

      const detailsResults = await this.multicallStaking(detailCalls);

      const userStakes: StakeInfo[] = [];
      let totalStaked = 0n;
      let totalRewards = 0n;
      let earliestExpiry = Infinity;

      activeStakeIndices.forEach((idx, i) => {
        const stakeInfo = stakeInfos[Number(idx)].result;
        
        // Results are in triplets: pending, maturity, penalty
        const pending = detailsResults[i * 3]?.result || 0n;
        const maturity = detailsResults[i * 3 + 1]?.result;
        const penalty = detailsResults[i * 3 + 2]?.result;

        const stAmount = stakeInfo?.amount ?? stakeInfo?.[0] ?? 0n;
        const remainingSeconds = Number(maturity?.remainingTime ?? maturity?.[0] ?? 0);
        const isMatured = maturity?.isMatured ?? maturity?.[1] ?? false;
        const pPercent = Number(penalty?.penaltyPercent ?? penalty?.[0] ?? 0);
        const pAmount = penalty?.penaltyAmount ?? penalty?.[1] ?? 0n;

        totalStaked += stAmount;
        totalRewards += pending;

        if (!isMatured && remainingSeconds > 0) {
          earliestExpiry = Math.min(earliestExpiry, remainingSeconds);
        }

        const claimableAmount = pending;
        const netWithdrawAmount = stAmount - pAmount + pending;

        userStakes.push({
          index: Number(idx),
          amount: formatUnits(stAmount, 18),
          pendingRewards: formatUnits(pending, 18),
          isMatured: isMatured,
          remainingSeconds: remainingSeconds,
          formattedCountdown: this.formatTime(remainingSeconds),
          progressPercent: isMatured ? 100 : 50,
          penaltyPercent: pPercent,
          penaltyAmount: formatUnits(pAmount, 18),
          claimableAmount: formatUnits(claimableAmount, 18),
          netWithdrawAmount: formatUnits(netWithdrawAmount, 18)
        });
      });

      // Convert remainingSeconds to an actual Unix timestamp in seconds
      const nowSec = Math.floor(Date.now() / 1000);
      const expiryTimestamp = earliestExpiry === Infinity ? 0 : nowSec + earliestExpiry;

      return {
        totalUserStaked: formatUnits(totalStaked, 18),
        totalUserRewards: formatUnits(totalRewards, 18),
        userPoolExpiry: expiryTimestamp,
        userStakeCount: userStakes.length,
        userStakes: userStakes
      };
    } catch (e) {
      console.error('Error fetching user pool aggregates', e);
      return {
        totalUserStaked: '0',
        totalUserRewards: '0',
        userPoolExpiry: 0,
        userStakeCount: 0,
        userStakes: []
      };
    }
  }

  private formatTime(seconds: number): string {
    if (seconds <= 0) return 'Matured';
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const parts: string[] = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0 || parts.length === 0) parts.push(`${s}s`); // Always show seconds if no other parts, or if seconds is 0

    return parts.join(' ');
  }

  /**
   * Universal Read Method for Staking Contract
   */
  public async readStakingContract(functionName: string, args: any[] = []): Promise<any> {
    const config = this.web3Service.getWagmiConfig();
    return this.withTimeout(readContract(config, {
      address: CONTRACT_ADDRESSES.LOONY_STAKING,
      abi: STAKING_ABI as any,
      functionName: functionName as any,
      args: args as any
    }));
  }

  /**
   * Wrapper with timeout for contract calls
   */
  private withTimeout<T>(promise: Promise<T>, ms = 15000): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout after ${ms}ms`)), ms)
      )
    ]);
  }

  /**
   * Multicall Read Helper
   */
  public async multicallStaking(calls: { functionName: string, args?: any[] }[]): Promise<any[]> {
    const config = this.web3Service.getWagmiConfig();
    return this.withTimeout(import('@wagmi/core').then(m => m.multicall(config, {
      contracts: calls.map(call => ({
        address: CONTRACT_ADDRESSES.LOONY_STAKING,
        abi: STAKING_ABI as any,
        functionName: call.functionName,
        args: call.args
      }))
    })));
  }

  /**
   * Universal Write Method for Staking Contract
   */
  public async writeStakingContract(functionName: string, args: any[] = []): Promise<string> {
    const config = this.web3Service.getWagmiConfig();
    const hash = await writeContract(config, {
      address: CONTRACT_ADDRESSES.LOONY_STAKING,
      abi: STAKING_ABI as any,
      functionName: functionName as any,
      args: args as any
    });
    
    // Wait for transaction receipt
    await waitForTransactionReceipt(config, { hash });
    return hash;
  }

  /**
   * ERC20 Token Helper methods
   */
  public async checkAllowance(userAddress: `0x${string}`, amountNeeded: bigint): Promise<boolean> {
    const config = this.web3Service.getWagmiConfig();
    const allowance: any = await readContract(config, {
      address: CONTRACT_ADDRESSES.LOONY_TOKEN,
      abi: TOKEN_ABI,
      functionName: 'allowance',
      args: [userAddress, CONTRACT_ADDRESSES.LOONY_STAKING]
    });
    return allowance >= amountNeeded;
  }

  public async approveToken(amount: bigint): Promise<string> {
    const config = this.web3Service.getWagmiConfig();
    const hash = await writeContract(config, {
      address: CONTRACT_ADDRESSES.LOONY_TOKEN,
      abi: TOKEN_ABI,
      functionName: 'approve',
      args: [CONTRACT_ADDRESSES.LOONY_STAKING, amount]
    });
    await waitForTransactionReceipt(config, { hash });
    return hash;
  }

  /**
   * Advanced Stake method combining Approve + Stake
   */
  public async doStake(poolId: bigint, amount: bigint) {
    const address = this.web3Service.userAddress();
    if (!address) throw new Error('Wallet not connected');

    const hasAllowance = await this.checkAllowance(address as `0x${string}`, amount);
    if (!hasAllowance) {
      await this.approveToken(amount);
    }

    return this.writeStakingContract('stake', [poolId, amount]);
  }
}
